import React, { useEffect, useMemo, useState } from 'react';
import {
  Button,
  Dropdown,
  Form,
  Label,
  Pagination,
  Popup,
  Table,
} from 'semantic-ui-react';
import { Link } from 'react-router-dom';
import { API, showError, showSuccess } from '../helpers';
import { useTranslation } from 'react-i18next';

import { ITEMS_PER_PAGE } from '../constants';
import {
  renderGroup,
  renderNumber,
  renderQuota,
  renderText,
} from '../helpers/render';

function renderRole(role, t) {
  switch (role) {
    case 1:
      return <Label>{t('user.table.role_types.normal')}</Label>;
    case 10:
      return <Label color='yellow'>{t('user.table.role_types.admin')}</Label>;
    case 100:
      return <Label color='orange'>{t('user.table.role_types.super_admin')}</Label>;
    default:
      return <Label color='red'>{t('user.table.role_types.unknown')}</Label>;
  }
}

function renderStatus(status, t) {
  switch (status) {
    case 1:
      return <Label basic>{t('user.table.status_types.activated')}</Label>;
    case 2:
      return (
        <Label basic color='red'>
          {t('user.table.status_types.banned')}
        </Label>
      );
    default:
      return (
        <Label basic color='grey'>
          {t('user.table.status_types.unknown')}
        </Label>
      );
  }
}

const roleFilterOptions = [
  { key: 'all', text: 'All Roles', value: '0' },
  { key: 'user', text: 'Normal User', value: '1' },
  { key: 'admin', text: 'Admin', value: '10' },
  { key: 'root', text: 'Root', value: '100' },
];

const statusFilterOptions = [
  { key: 'all', text: 'All Status', value: '0' },
  { key: 'enabled', text: 'Enabled', value: '1' },
  { key: 'disabled', text: 'Disabled', value: '2' },
];

const UsersTable = () => {
  const { t } = useTranslation();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activePage, setActivePage] = useState(1);
  const [searchKeyword, setSearchKeyword] = useState('');
  const [appliedKeyword, setAppliedKeyword] = useState('');
  const [searching, setSearching] = useState(false);
  const [orderBy, setOrderBy] = useState('');
  const [filters, setFilters] = useState({
    company: '',
    source: '',
    customerLevel: '',
    role: '0',
    status: '0',
    minRechargeAmount: '',
    maxRechargeAmount: '',
    minConsumeAmount: '',
    maxConsumeAmount: '',
  });

  const buildQueryString = (page) => {
    const params = new URLSearchParams();
    params.set('p', String(page));
    params.set('order', orderBy);
    if (appliedKeyword) {
      params.set('keyword', appliedKeyword);
    }
    if (filters.company) {
      params.set('company', filters.company);
    }
    if (filters.source) {
      params.set('source', filters.source);
    }
    if (filters.customerLevel) {
      params.set('customer_level', filters.customerLevel);
    }
    if (filters.role !== '0') {
      params.set('role', filters.role);
    }
    if (filters.status !== '0') {
      params.set('status', filters.status);
    }
    if (filters.minRechargeAmount) {
      params.set('min_recharge_amount', filters.minRechargeAmount);
    }
    if (filters.maxRechargeAmount) {
      params.set('max_recharge_amount', filters.maxRechargeAmount);
    }
    if (filters.minConsumeAmount) {
      params.set('min_consume_amount', filters.minConsumeAmount);
    }
    if (filters.maxConsumeAmount) {
      params.set('max_consume_amount', filters.maxConsumeAmount);
    }
    return params.toString();
  };

  const loadUsers = async (page) => {
    setLoading(true);
    const res = await API.get(`/api/user/?${buildQueryString(page)}`);
    const { success, message, data } = res.data;
    if (success) {
      if (page === 0) {
        setUsers(data);
      } else {
        setUsers((currentUsers) => [...currentUsers, ...data]);
      }
    } else {
      showError(message);
    }
    setLoading(false);
  };

  useEffect(() => {
    loadUsers(0)
      .then()
      .catch((reason) => {
        showError(reason);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderBy, appliedKeyword, filters]);

  const onPaginationChange = (e, { activePage: nextPage }) => {
    (async () => {
      if (nextPage === Math.ceil(users.length / ITEMS_PER_PAGE) + 1) {
        await loadUsers(nextPage - 1);
      }
      setActivePage(nextPage);
    })();
  };

  const manageUser = (username, action, idx) => {
    (async () => {
      const res = await API.post('/api/user/manage', {
        username,
        action,
      });
      const { success, message, data } = res.data;
      if (!success) {
        showError(message);
        return;
      }
      showSuccess(t('user.messages.operation_success'));
      setUsers((currentUsers) => {
        const nextUsers = [...currentUsers];
        const realIdx = (activePage - 1) * ITEMS_PER_PAGE + idx;
        if (action === 'delete') {
          nextUsers[realIdx].deleted = true;
        } else {
          nextUsers[realIdx].status = data.status;
          nextUsers[realIdx].role = data.role;
        }
        return nextUsers;
      });
    })();
  };

  const searchUsers = async () => {
    setSearching(true);
    setActivePage(1);
    setAppliedKeyword(searchKeyword.trim());
    setSearching(false);
  };

  const handleKeywordChange = (e, { value }) => {
    setSearchKeyword(value.trim());
  };

  const handleFilterChange = (e, { name, value }) => {
    setFilters((current) => ({
      ...current,
      [name]: value,
    }));
    setActivePage(1);
  };

  const sortUser = (key) => {
    if (users.length === 0) return;
    setLoading(true);
    const sortedUsers = [...users];
    sortedUsers.sort((a, b) => {
      if (!isNaN(a[key])) {
        return a[key] - b[key];
      }
      return ('' + a[key]).localeCompare(b[key]);
    });
    if (sortedUsers[0].id === users[0].id) {
      sortedUsers.reverse();
    }
    setUsers(sortedUsers);
    setLoading(false);
  };

  const handleOrderByChange = (e, { value }) => {
    setOrderBy(value);
    setActivePage(1);
  };

  const visibleUsers = useMemo(
    () =>
      users.slice((activePage - 1) * ITEMS_PER_PAGE, activePage * ITEMS_PER_PAGE),
    [activePage, users]
  );

  return (
    <>
      <Form onSubmit={searchUsers}>
        <Form.Input
          icon='search'
          fluid
          iconPosition='left'
          placeholder={t('user.search')}
          value={searchKeyword}
          loading={searching}
          onChange={handleKeywordChange}
        />
        <Form.Group widths='equal'>
          <Form.Input
            label='Company'
            name='company'
            placeholder='Company'
            value={filters.company}
            onChange={handleFilterChange}
          />
          <Form.Input
            label='Source'
            name='source'
            placeholder='Source'
            value={filters.source}
            onChange={handleFilterChange}
          />
          <Form.Input
            label='Customer Level'
            name='customerLevel'
            placeholder='Customer Level'
            value={filters.customerLevel}
            onChange={handleFilterChange}
          />
        </Form.Group>
        <Form.Group widths='equal'>
          <Form.Dropdown
            label='Role'
            selection
            options={roleFilterOptions}
            name='role'
            value={filters.role}
            onChange={handleFilterChange}
          />
          <Form.Dropdown
            label='Status'
            selection
            options={statusFilterOptions}
            name='status'
            value={filters.status}
            onChange={handleFilterChange}
          />
          <Form.Input
            label='Min Recharge (cent)'
            name='minRechargeAmount'
            type='number'
            value={filters.minRechargeAmount}
            onChange={handleFilterChange}
          />
          <Form.Input
            label='Max Recharge (cent)'
            name='maxRechargeAmount'
            type='number'
            value={filters.maxRechargeAmount}
            onChange={handleFilterChange}
          />
        </Form.Group>
        <Form.Group widths='equal'>
          <Form.Input
            label='Min Consume (cent)'
            name='minConsumeAmount'
            type='number'
            value={filters.minConsumeAmount}
            onChange={handleFilterChange}
          />
          <Form.Input
            label='Max Consume (cent)'
            name='maxConsumeAmount'
            type='number'
            value={filters.maxConsumeAmount}
            onChange={handleFilterChange}
          />
        </Form.Group>
      </Form>

      <Table basic='very' compact size='small'>
        <Table.Header>
          <Table.Row>
            <Table.HeaderCell style={{ cursor: 'pointer' }} onClick={() => sortUser('id')}>
              {t('user.table.id')}
            </Table.HeaderCell>
            <Table.HeaderCell style={{ cursor: 'pointer' }} onClick={() => sortUser('username')}>
              {t('user.table.username')}
            </Table.HeaderCell>
            <Table.HeaderCell style={{ cursor: 'pointer' }} onClick={() => sortUser('group')}>
              {t('user.table.group')}
            </Table.HeaderCell>
            <Table.HeaderCell style={{ cursor: 'pointer' }} onClick={() => sortUser('quota')}>
              {t('user.table.quota')}
            </Table.HeaderCell>
            <Table.HeaderCell style={{ cursor: 'pointer' }} onClick={() => sortUser('company')}>
              Business
            </Table.HeaderCell>
            <Table.HeaderCell style={{ cursor: 'pointer' }} onClick={() => sortUser('role')}>
              {t('user.table.role_text')}
            </Table.HeaderCell>
            <Table.HeaderCell style={{ cursor: 'pointer' }} onClick={() => sortUser('status')}>
              {t('user.table.status_text')}
            </Table.HeaderCell>
            <Table.HeaderCell>{t('user.table.actions')}</Table.HeaderCell>
          </Table.Row>
        </Table.Header>

        <Table.Body>
          {visibleUsers.map((user, idx) => {
            if (user.deleted) return <React.Fragment key={user.id} />;
            return (
              <Table.Row key={user.id}>
                <Table.Cell>{user.id}</Table.Cell>
                <Table.Cell>
                  <Popup
                    content={user.email ? user.email : 'No email bound'}
                    key={user.username}
                    header={user.display_name ? user.display_name : user.username}
                    trigger={<span>{renderText(user.username, 15)}</span>}
                    hoverable
                  />
                </Table.Cell>
                <Table.Cell>{renderGroup(user.group)}</Table.Cell>
                <Table.Cell>
                  <Popup
                    content={t('user.table.remaining_quota')}
                    trigger={<Label basic>{renderQuota(user.quota, t)}</Label>}
                  />
                  <Popup
                    content={t('user.table.used_quota')}
                    trigger={<Label basic>{renderQuota(user.used_quota, t)}</Label>}
                  />
                  <Popup
                    content={t('user.table.request_count')}
                    trigger={<Label basic>{renderNumber(user.request_count)}</Label>}
                  />
                </Table.Cell>
                <Table.Cell>
                  {user.company ? <Label basic>{renderText(user.company, 14)}</Label> : null}
                  {user.mobile ? <Label basic>{renderText(user.mobile, 14)}</Label> : null}
                  {user.customer_level ? (
                    <Label basic color='blue'>
                      {user.customer_level}
                    </Label>
                  ) : null}
                  {user.source ? (
                    <Label basic color='teal'>
                      {user.source}
                    </Label>
                  ) : null}
                </Table.Cell>
                <Table.Cell>{renderRole(user.role, t)}</Table.Cell>
                <Table.Cell>{renderStatus(user.status, t)}</Table.Cell>
                <Table.Cell>
                  <div>
                    <Button
                      size='tiny'
                      positive
                      onClick={() => manageUser(user.username, 'promote', idx)}
                      disabled={user.role === 100}
                    >
                      {t('user.buttons.promote')}
                    </Button>
                    <Button
                      size='tiny'
                      color='yellow'
                      onClick={() => manageUser(user.username, 'demote', idx)}
                      disabled={user.role === 100}
                    >
                      {t('user.buttons.demote')}
                    </Button>
                    <Popup
                      trigger={
                        <Button size='tiny' negative disabled={user.role === 100}>
                          {t('user.buttons.delete')}
                        </Button>
                      }
                      on='click'
                      flowing
                      hoverable
                    >
                      <Button
                        negative
                        size='tiny'
                        onClick={() => manageUser(user.username, 'delete', idx)}
                      >
                        {t('user.buttons.delete_user')} {user.username}
                      </Button>
                    </Popup>
                    <Button
                      size='tiny'
                      onClick={() =>
                        manageUser(
                          user.username,
                          user.status === 1 ? 'disable' : 'enable',
                          idx
                        )
                      }
                      disabled={user.role === 100}
                    >
                      {user.status === 1 ? t('user.buttons.disable') : t('user.buttons.enable')}
                    </Button>
                    <Button size='tiny' as={Link} to={'/user/edit/' + user.id}>
                      {t('user.buttons.edit')}
                    </Button>
                  </div>
                </Table.Cell>
              </Table.Row>
            );
          })}
        </Table.Body>

        <Table.Footer>
          <Table.Row>
            <Table.HeaderCell colSpan='8'>
              <Button size='small' as={Link} to='/user/add' loading={loading}>
                {t('user.buttons.add')}
              </Button>
              <Dropdown
                placeholder={t('user.table.sort_by')}
                selection
                options={[
                  { key: '', text: t('user.table.sort.default'), value: '' },
                  {
                    key: 'quota',
                    text: t('user.table.sort.by_quota'),
                    value: 'quota',
                  },
                  {
                    key: 'used_quota',
                    text: t('user.table.sort.by_used_quota'),
                    value: 'used_quota',
                  },
                  {
                    key: 'request_count',
                    text: t('user.table.sort.by_request_count'),
                    value: 'request_count',
                  },
                  {
                    key: 'total_recharge_amount',
                    text: 'Sort by Recharge',
                    value: 'total_recharge_amount',
                  },
                  {
                    key: 'total_consume_amount',
                    text: 'Sort by Consume',
                    value: 'total_consume_amount',
                  },
                ]}
                value={orderBy}
                onChange={handleOrderByChange}
                style={{ marginLeft: '10px' }}
              />
              <Pagination
                floated='right'
                activePage={activePage}
                onPageChange={onPaginationChange}
                size='small'
                siblingRange={1}
                totalPages={
                  Math.ceil(users.length / ITEMS_PER_PAGE) +
                  (users.length % ITEMS_PER_PAGE === 0 ? 1 : 0)
                }
              />
            </Table.HeaderCell>
          </Table.Row>
        </Table.Footer>
      </Table>
    </>
  );
};

export default UsersTable;
